import { useId, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { MembershipContext, MembershipPeriod } from "@/api/ministryMemberships";
import { operationError } from "@/api/ministryOperations";
import { useMemberMemberships, useMembershipMutation } from "./queries";

const fieldClass = "w-full rounded-lg border border-slate-700 bg-slate-950 p-3 text-white";
const buttonClass = "rounded-lg border border-indigo-500/40 px-3 py-2 text-sm text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-40";

function localNow() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 19);
}

function formatTime(value: string) {
  return new Date(value).toLocaleString();
}

type Editor = { action: "join"; ministryId: string } | { action: "leave"; ministryId: string; period: MembershipPeriod };
type Props = { churchId: string; memberId: string; memberName: string; compact?: boolean };

export function MemberMinistryMemberships({ churchId, memberId, memberName, compact = false }: Props) {
  const sectionId = useId();
  const [expanded, setExpanded] = useState(!compact);
  const [history, setHistory] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [effective, setEffective] = useState(localNow);
  const client = useQueryClient();
  const mutation = useMembershipMutation(client, churchId);
  const { contexts, groups } = useMemberMemberships(churchId, memberId);
  const loading = contexts.isPending || groups.some(group => group.query.isPending);
  const error = contexts.error ?? groups.find(group => group.query.error)?.query.error;
  const available = contexts.isError ? [] : (contexts.data ?? []);
  const managed = available.filter(context => context.can_manage);
  // The domain does not permit future join/leave dates. An open period is active.
  const active = groups.flatMap(group => group.query.isError ? [] :
    (group.query.data ?? []).filter(period => !period.left_at).map(period => ({ period, context: group.context })));
  const canAdd = managed.filter(context => !active.some(item => item.context.ministry_id === context.ministry_id));
  const selectedContext = available.find(context => context.ministry_id === editor?.ministryId);

  function openEditor(next: Editor) {
    mutation.reset();
    setEffective(localNow());
    setEditor(next);
    setExpanded(true);
  }

  function save() {
    if (!editor || !selectedContext?.can_manage) return;
    const effectiveAt = new Date(effective).toISOString();
    const change = editor.action === "leave"
      ? { action: "leave" as const, ministryId: editor.ministryId, membershipId: editor.period.id, effectiveAt }
      : { action: "join" as const, ministryId: editor.ministryId, memberId, effectiveAt };
    mutation.mutate(change, { onSuccess: () => setEditor(null) });
  }

  return (
    <section aria-label={`Ministry memberships for ${memberName}`} className={compact ? "mt-3 space-y-3" : "space-y-4 rounded-2xl border border-slate-800 bg-slate-900/50 p-6"}>
      {!compact && <h2 className="text-xl font-semibold text-white">Ministry Memberships</h2>}
      {loading && <p className="text-sm text-slate-400">Loading ministry memberships…</p>}
      {error && <p role="alert" className="text-sm text-rose-300">{operationError(error)}</p>}
      {!loading && !error && (
        <>
          {!available.length ? <p className="text-sm text-slate-400">No ministry memberships are available to view or manage with your access.</p> : (
            <div className="flex flex-wrap gap-2">
              {active.map(({ period, context }) => <span key={period.id} className="rounded-full bg-indigo-500/15 px-3 py-1 text-sm text-indigo-200">{context.name}{!context.is_active && " (archived ministry)"}</span>)}
              {!active.length && <p className="text-sm text-slate-400">No active ministry memberships in your permitted view.</p>}
            </div>
          )}
          {compact && available.length > 0 && (
            <button type="button" className={buttonClass} aria-expanded={expanded} aria-controls={sectionId} onClick={() => setExpanded(value => !value)}>
              {expanded ? "Close membership details" : managed.length ? "Manage ministry memberships" : "View ministry memberships"}
            </button>
          )}
        </>
      )}
      {expanded && !loading && !error && (
        <div id={sectionId} className="space-y-4">
          <p className="text-sm text-slate-400">Membership belongs to {memberName}'s existing profile. Ending membership preserves past membership and attendance. Only ministries within your access are shown.</p>
          {groups.map(({ context, query }) => {
            const periods = query.data ?? [];
            const rows = history ? [...periods].reverse() : periods.filter(period => !period.left_at);
            if (!rows.length) return null;
            return <MinistryPeriods key={context.ministry_id} context={context} periods={rows} hasActive={periods.some(period => !period.left_at)} disabled={mutation.isPending}
              onLeave={period => openEditor({ action: "leave", ministryId: context.ministry_id, period })}
              onRejoin={() => openEditor({ action: "join", ministryId: context.ministry_id })} />;
          })}
          {groups.length > 0 && <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={history} onChange={event => setHistory(event.target.checked)} />Show membership history</label>}
          {available.some(context => !context.can_view) && <p className="text-sm text-slate-400">Some ministries allow enrollment but do not grant membership-history visibility.</p>}
          {canAdd.length > 0 && !editor && <button type="button" className={buttonClass} onClick={() => openEditor({ action: "join", ministryId: canAdd[0].ministry_id })}>Add to ministry</button>}
          {editor && (
            <form className="space-y-3 rounded-xl border border-slate-700 p-4" onSubmit={event => { event.preventDefault(); save(); }}>
              {editor.action === "join" ? (
                <label className="block space-y-1 text-sm text-slate-300">Ministry
                  <select required className={fieldClass} value={editor.ministryId} disabled={mutation.isPending} onChange={event => {mutation.reset(); setEditor({action: "join", ministryId: event.target.value});}}>
                    {canAdd.map(context => <option key={context.ministry_id} value={context.ministry_id}>{context.name}</option>)}
                  </select>
                </label>
              ) : <p className="text-sm text-slate-300">End {memberName}'s membership in {selectedContext?.name}? Historical records will be retained.</p>}
              <label className="block space-y-1 text-sm text-slate-300">Effective date and time (your local time)
                <input required type="datetime-local" step="1" className={fieldClass} value={effective} disabled={mutation.isPending} onChange={event => setEffective(event.target.value)} />
              </label>
              <p className="text-xs text-slate-400">Service eligibility uses this membership period at the occurrence start time. Future membership dates are not supported.</p>
              {mutation.error && <p role="alert" className="text-sm text-rose-300">{operationError(mutation.error)}</p>}
              <div className="flex flex-wrap gap-2">
                <button type="submit" className={buttonClass} disabled={mutation.isPending || !selectedContext?.can_manage || !effective}>
                  {mutation.isPending ? "Saving…" : editor.action === "leave" ? "Confirm end membership" : `Enroll in ${selectedContext?.name ?? "ministry"}`}
                </button>
                <button type="button" className={buttonClass} disabled={mutation.isPending} onClick={() => {setEditor(null); mutation.reset();}}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  );
}

function MinistryPeriods({ context, periods, hasActive, disabled, onLeave, onRejoin }: {
  context: MembershipContext; periods: MembershipPeriod[]; hasActive: boolean; disabled: boolean;
  onLeave: (period: MembershipPeriod) => void; onRejoin: () => void;
}) {
  return <div className="space-y-3 rounded-xl border border-slate-800 p-4">
    <h3 className="font-semibold text-white">{context.name}{!context.is_active && " (archived ministry)"}</h3>
    {periods.map(period => <div key={period.id} className="flex flex-wrap items-center justify-between gap-3 text-sm">
      <div><p className={period.left_at ? "text-slate-400" : "text-emerald-300"}>{period.left_at ? "Inactive" : "Active"}</p><p className="text-slate-400">Joined {formatTime(period.joined_at)}{period.left_at && ` · Ended ${formatTime(period.left_at)}`}</p></div>
      {!period.left_at && context.can_manage && <button type="button" disabled={disabled} className={buttonClass} onClick={() => onLeave(period)}>End membership</button>}
    </div>)}
    {!hasActive && context.can_manage && <button type="button" disabled={disabled} className={buttonClass} onClick={onRejoin}>Rejoin</button>}
  </div>;
}
